import { type PVZPointV2Optional } from '@/types/IFetchPvzList';
import { type IGetUserCity } from '@/types/IGetUserCity';
import {
  type DeliveryInfo,
  type OrderRecipient
} from '@/types/FunctionalModal';
import { type IErrorData } from '@/utils/getErrorData';

export interface IDeliveryValidationErrors {
  type: boolean;
  provider: boolean;
  pvz: boolean;
  address: boolean;
}

export interface IPvzManagementState {
  pvzList: PVZPointV2Optional[];
  pvzSearch: string;
  showPvzSuggestions: boolean;
  selectedPvzId: number | null;
  isLoadingPvz: boolean;
  pvzError: string | null;
}

export interface IAddressManagementState {
  addressEdit: string;
  addressSuggestions: IGetUserCity[];
  showAddressSuggestions: boolean;
  isLoadingAddress: boolean;
  addressError: string | null;
}

export interface IFormCoordinationState {
  deliveryInfo: DeliveryInfo;
  recipientData: OrderRecipient;
  editedRecipientData: OrderRecipient;
  pendingUpdates: Partial<DeliveryInfo>;
  isUpdating: boolean;
}

export interface IDeliveryEditState {
  formCoordination: IFormCoordinationState;
  pvzManagement: IPvzManagementState;
  addressManagement: IAddressManagementState;
  validationErrors: IDeliveryValidationErrors;
  cache: ICacheState;
  isLoading: boolean;
  error: IErrorData | null;
  settings: {
    isCustomerRedactor: boolean;
    recipientAddress: string;
    recipientCity: string;
    classNameForInput: string;
    assessedCost: number | undefined;
  };
}

export interface IDeliveryEditInitParams {
  deliveryInfo: DeliveryInfo;
  isCustomerRedactor?: boolean;
  recipientAddress?: string;
  recipientCity?: string;
  classNameForInput?: string;
  assessedCost?: number | undefined;
  validationErrors?: Partial<IDeliveryValidationErrors>;
}

export interface IFetchPvzListParams {
  city: string;
  provider: string;
  type: string;
}

export interface ISelectPvzParams {
  point: PVZPointV2Optional;
}

export type DeliveryInfoUpdate = Partial<DeliveryInfo>;
export type PvzManagementUpdate = Partial<IPvzManagementState>;
export type AddressManagementUpdate = Partial<IAddressManagementState>;
export type FormCoordinationUpdate = Partial<IFormCoordinationState>;
export type ValidationErrorsUpdate = Partial<IDeliveryValidationErrors>;

export const initialPvzManagementState: IPvzManagementState = {
  pvzList: [],
  pvzSearch: '',
  showPvzSuggestions: false,
  selectedPvzId: null,
  isLoadingPvz: false,
  pvzError: null
};

export const initialAddressManagementState: IAddressManagementState = {
  addressEdit: '',
  addressSuggestions: [],
  showAddressSuggestions: false,
  isLoadingAddress: false,
  addressError: null
};

export const initialValidationErrors: IDeliveryValidationErrors = {
  type: false,
  provider: false,
  pvz: false,
  address: false
};

// Интерфейсы для кеширования
export interface ICachedPvzData {
  pvzList: PVZPointV2Optional[];
  timestamp: number;
  city: string;
  provider: string;
  type: string;
}

export interface ICacheState {
  pvzCache: Record<string, ICachedPvzData>;
  cacheTimestamp: number;
  currentCacheKey: string | null;
}

export const initialCacheState: ICacheState = {
  pvzCache: {},
  cacheTimestamp: 0,
  currentCacheKey: null
};
